import { useRef, type ChangeEvent } from 'react'
import { cn } from '@lib/utils/helpers'

import type { PaymentFileUploadFieldProps } from '@/features/company/types'

export function PaymentFileUploadField({
    disabled,
    isUploading,
    chosenDisplayName,
    onFilesSelected,
    className,
    'aria-label': ariaLabel,
    testIdPrefix,
}: PaymentFileUploadFieldProps) {
    const inputRef = useRef<HTMLInputElement>(null)
    const openPicker = () => {
        if (!disabled && !isUploading) {
            inputRef.current?.click()
        }
    }
    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        onFilesSelected(e.target.files)
        e.target.value = ''
    }
    const showText = isUploading ? 'Uploading…' : chosenDisplayName.trim() || 'Choose file...'
    return (
        <div
            className={cn(
                'flex min-h-[2.5rem] overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm',
                className,
            )}
        >
            <button
                type="button"
                data-testid={testIdPrefix ? `${testIdPrefix}-choose-trigger` : undefined}
                disabled={disabled || isUploading}
                aria-label={ariaLabel}
                onClick={openPicker}
                className={cn(
                    'min-w-0 flex-1 truncate px-3 py-2 text-left text-sm transition',
                    chosenDisplayName.trim() || isUploading ? 'text-slate-700' : 'text-slate-400',
                    'hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60',
                )}
            >
                {showText}
            </button>
            <input
                ref={inputRef}
                type="file"
                data-testid={testIdPrefix ? `${testIdPrefix}-file-input` : undefined}
                className="sr-only"
                disabled={disabled || isUploading}
                onChange={onInputChange}
            />
            <button
                type="button"
                data-testid={testIdPrefix ? `${testIdPrefix}-upload-button` : undefined}
                disabled={disabled || isUploading}
                onClick={openPicker}
                className="shrink-0 border-l border-slate-300 bg-[rgb(21_151_255)]/10 px-4 py-2 text-sm font-semibold text-[rgb(21_151_255)] transition hover:bg-[rgb(21_151_255)]/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60"
            >
                Upload
            </button>
        </div>
    )
}
